// klaviyo-back-in-stock.js

const KLAVIYO_ENDPOINT = "https://a.klaviyo.com/client/back-in-stock-subscriptions/";
const KLAVIYO_REVISION = "2024-07-15";

const FALLBACK_MESSAGES = {
  success: "Success! You will be notified when the product is back in stock.",
  error: "Unable to process your request. Please try again later.",
  sending: "Sending...",
};

export class KlaviyoBackInStock extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form.notify-form");
    this.messageContainer = this.querySelector(".form-message");

    if (!this.form || !this.messageContainer) return;

    this.apiKey = this.form.dataset.klaviyoApiKey;

    // Already bound — the element was moved rather than re-created.
    if (!this.apiKey || this.abortController) return;

    this.abortController = new AbortController();
    this.form.addEventListener("submit", (event) => this.handleSubmit(event), {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  get messages() {
    const data = this.form.dataset;

    return {
      success: data.formSuccessMessage || FALLBACK_MESSAGES.success,
      error: data.formErrorMessage || FALLBACK_MESSAGES.error,
      sending: data.formSendingMessage || FALLBACK_MESSAGES.sending,
    };
  }

  /**
   * Read live rather than cached: product-form-handler rewrites this attribute whenever the
   * shopper picks another variant.
   */
  get variantId() {
    return this.form.dataset.productId;
  }

  async handleSubmit(event) {
    event.preventDefault();
    // This form often sits inside a container with the product form. Delegated submit handlers
    // on body/document must not see this submit and mistake it for an add to cart.
    event.stopPropagation();

    const emailInput = this.form.querySelector('input[type="email"]');
    const notifyButton = this.form.querySelector("button");
    const { success, error, sending } = this.messages;

    if (!emailInput || !this.variantId) {
      this.showMessage(error, "error");
      return;
    }

    this.setLoadingState(notifyButton, sending, true);

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${this.apiKey}`, {
        method: "POST",
        headers: {
          revision: KLAVIYO_REVISION,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(this.buildPayload(emailInput.value)),
      });

      if (response.status === 202) {
        this.showMessage(success, "success");
        emailInput.value = "";
      } else {
        const data = await response.json().catch(() => ({}));
        this.showMessage(data.errors?.[0]?.detail || data.error_message || error, "error");
      }
    } catch {
      this.showMessage(error, "error");
    } finally {
      this.setLoadingState(notifyButton, sending, false);
    }
  }

  buildPayload(email) {
    return {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          channels: ["EMAIL"],
          profile: {
            data: {
              type: "profile",
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${this.variantId}`,
            },
          },
        },
      },
    };
  }

  setLoadingState(button, sendingText, isLoading) {
    if (!button) return;

    if (isLoading) {
      // Stash the rendered label so the locale string stays the single source of truth.
      this.buttonLabel = button.textContent;
      button.textContent = sendingText;
    } else {
      button.textContent = this.buttonLabel ?? button.textContent;
    }

    button.disabled = isLoading;
  }

  showMessage(message, state) {
    if (!this.messageContainer) return;

    this.messageContainer.textContent = message;
    this.messageContainer.classList.toggle("success-message", state === "success");
    this.messageContainer.classList.toggle("error-message", state === "error");
  }
}

if (!customElements.get("klaviyo-bis-form")) {
  customElements.define("klaviyo-bis-form", KlaviyoBackInStock);
}
